/**
 * LearnerHomeworkList — Phase 3
 *
 * Renders the learner's active homework assignments below the framework
 * strip on `/learn`. Each row links to the lesson + exposes Complete /
 * Dismiss actions. Recommended assignments are highlighted at the top.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, CheckCircle2, X, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import RoundaboutTriggerCard from "./RoundaboutTriggerCard";
import AsyncSurface from "../AsyncSurface";

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_LABEL = {
  stage_unlock:    "Stage unlocked",
  foundation_gap:  "Foundation refresher",
  manual:          "From your instructor",
  evidence_homework: "Preparation from your learning",
};
const SOURCE_TONE = {
  stage_unlock:    "bg-indigo-50 text-indigo-700 ring-indigo-200",
  foundation_gap:  "bg-amber-50 text-amber-700 ring-amber-200",
  manual:          "bg-emerald-50 text-emerald-700 ring-emerald-200",
};

export default function LearnerHomeworkList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // Phase 1.3 alive-contract migration (May 19, 2026) — previously
  // the catch swallowed fetch failures into a silent `items=[]`
  // which collapsed "no homework yet" and "the framework API
  // failed" into the same null return. Now error is tracked so a
  // failed reading-list fetch surfaces calmly.
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/learner-framework/me/homework`, {
        withCredentials: true,
      });
      setItems(data.items || []);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Couldn't load your reading list just now."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleComplete(id) {
    setBusyId(id);
    try {
      await axios.post(`${API}/api/learner-framework/me/homework/${id}/complete`, {}, {
        withCredentials: true,
      });
      toast.success("Marked as read.");
      await load();
    } catch (e) {
      toast.error("Couldn't mark as read", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyId(null);
    }
  }

  async function handleDismiss(id) {
    setBusyId(id);
    try {
      await axios.post(`${API}/api/learner-framework/me/homework/${id}/dismiss`, {}, {
        withCredentials: true,
      });
      await load();
    } catch (e) {
      toast.error("Couldn't dismiss", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 p-4">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your reading list…
        </div>
      </div>
    );
  }
  // Truthful failure surface — calm slate, retry available.
  if (error) {
    return (
      <AsyncSurface
        testid="learner-homework"
        error={error}
        errorLabel={error}
        onRetry={load}
      >
        {null}
      </AsyncSurface>
    );
  }
  // Calm-baseline silence when there's genuinely nothing assigned.
  if (!items.length) return null;

  return (
    <div data-testid="learner-homework-list" className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-indigo-600" />
          <h3 className="text-sm font-bold text-slate-900">Your reading list</h3>
        </div>
        <span className="text-xs text-slate-500">{items.length}</span>
      </div>
      <ul className="divide-y divide-slate-100">
        {items.map((it) => (
          <li
            key={it.id}
            data-testid={`homework-item-${it.id}`}
            className={`px-4 py-3 ${it.recommended ? "bg-amber-50/60" : ""}`}
          >
            <div className="flex items-start justify-between gap-3">
              <button
                onClick={() => nav(it.source === "evidence_homework" ? "/learn/homework" : it.lesson_id ? `/lesson/${it.lesson_id}` : "/learn")}
                className="flex-1 min-w-0 text-left"
              >
                <div className="flex items-center gap-2 mb-1">
                  {it.recommended && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-700">
                      <Sparkles className="w-3 h-3" /> Suggested next
                    </span>
                  )}
                  <span className={`inline-flex items-center text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ring-1 ${SOURCE_TONE[it.source] || "bg-slate-100 text-slate-600 ring-slate-200"}`}>
                    {SOURCE_LABEL[it.source] || it.source}
                  </span>
                </div>
                <div className="text-sm font-semibold text-slate-900 leading-snug truncate">
                  {it.lesson_title || "Lesson"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{it.reason}</div>
              </button>
              <ArrowRight className="w-4 h-4 text-slate-400 flex-none mt-1" />
            </div>
            <div className="mt-2 flex items-center gap-2">
              {it.source === "evidence_homework" ? (
                <button
                  data-testid={`homework-open-${it.id}`}
                  onClick={() => nav("/learn/homework")}
                  className="inline-flex min-h-11 items-center text-xs font-semibold rounded-lg bg-emerald-600 text-white px-3 py-1.5"
                >
                  {it.completed_at ? "View your saved response" : "Open preparation and respond"}
                </button>
              ) : <>
              <button
                data-testid={`homework-complete-${it.id}`}
                onClick={() => handleComplete(it.id)}
                disabled={busyId === it.id}
                className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 disabled:opacity-50"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Mark as read
              </button>
              <button
                data-testid={`homework-dismiss-${it.id}`}
                onClick={() => handleDismiss(it.id)}
                disabled={busyId === it.id}
                className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 disabled:opacity-50"
              >
                <X className="w-3.5 h-3.5" /> Not now
              </button>
              </>}
            </div>
            {/* Roundabout Trigger Learning — injects ONE understanding card
                inline when the lesson title matches a roundabout topic.
                Snoozes 7 days when the learner taps "Got it". */}
            <div className="mt-2.5">
              <RoundaboutTriggerCard lessonTitle={it.lesson_title} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

